import { collection, deleteDoc, doc, getDoc, setDoc, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { AutoIncrementService } from '@/v2/agents/helpers/auto-increment-service';
import { eduDb } from '@/firebase';
import { useAuthStore } from '@/v2/store/shared/auth-store';
import { Alert } from '@/v2/plugins/alert';
import { t } from '@/locales/index';

export class LearningPath {
	id = null;
	learningPathCode = null;
	learningPathName = null;
	description = null;
	learningPathObjectives = null;
	targetAudience = null;
	acquiredSkills = null;
	isDiploma = false;
	workshops = [];
	attachments = [];

	constructor(obj) {
		Object.keys(obj).forEach((key) => {
			this[key] = obj[key];
		});
	}

	static async getById(id) {
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;

		const learningPathDocRef = doc(eduDb, `tenants/${tenantId}/learning-paths`, id);

		const res = await getDoc(learningPathDocRef);
		return new LearningPath({ ...res.data() });
	}

	static async create(data) {
		Object.keys(data).forEach((key) => {
			if (data[key] === undefined) {
				delete data[key];
			}
		});
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;

		data.id = await AutoIncrementService.getNextId('learning-paths');

		const dataCollection = collection(eduDb, `tenants/${tenantId}/learning-paths`);
		await setDoc(doc(dataCollection, `${data.id}`), data);

		// Update workshops with this learning path
		if (data.workshops && data.workshops.length > 0) {
			const batch = writeBatch(eduDb);
			for (const workshop of data.workshops) {
				const workshopRef = doc(eduDb, `tenants/${tenantId}/workshops`, `${workshop.id}`);
				const workshopDoc = await getDoc(workshopRef);
				if (workshopDoc.exists()) {
					const workshopData = workshopDoc.data();
					const learningPaths = workshopData.learningPaths || [];
					learningPaths.push({
						id: data.id,
						learningPathName: data.learningPathName
					});
					batch.update(workshopRef, { learningPaths });
				}
			}
			await batch.commit();
		}
	}

	async update(data) {
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;

		const learningPathDocRef = doc(eduDb, `tenants/${tenantId}/learning-paths`, `${this.id}`);

		// Get current workshops
		const currentWorkshops = this.workshops || [];
		const newWorkshops = data.workshops || [];

		// Find removed workshops
		const removedWorkshops = currentWorkshops.filter(
			workshop => !newWorkshops.some(newWorkshop => +newWorkshop.id === +workshop.id)
		);

		// Find added workshops
		const addedWorkshops = newWorkshops.filter(
			workshop => !currentWorkshops.some(currentWorkshop => +currentWorkshop.id === +workshop.id)
		);
		// Update learning path document
		await setDoc(learningPathDocRef, data, { merge: true });

		// Update workshops' learning paths
		const batch = writeBatch(eduDb);

		// Remove learning path from removed workshops
		for (const workshop of removedWorkshops) {
			const workshopRef = doc(eduDb, `tenants/${tenantId}/workshops`, `${workshop.id}`);
			const workshopDoc = await getDoc(workshopRef);
			if (workshopDoc.exists()) {
				const workshopData = workshopDoc.data();
				const learningPaths = workshopData.learningPaths || [];
				const updatedLearningPaths = learningPaths.filter(
					lp => +lp.id !== +this.id
				);
				batch.update(workshopRef, { learningPaths: updatedLearningPaths });
			}
		}

		// Add learning path to added workshops
		for (const workshop of addedWorkshops) {
			const workshopRef = doc(eduDb, `tenants/${tenantId}/workshops`, `${workshop.id}`);
			const workshopDoc = await getDoc(workshopRef);
			if (workshopDoc.exists()) {
				const workshopData = workshopDoc.data();
				const learningPaths = workshopData.learningPaths || [];
				learningPaths.push({
					id: this.id,
					learningPathName: data.learningPathName || this.learningPathName
				});
				batch.update(workshopRef, { learningPaths });
			}
		}

		await batch.commit();
	}

	async remove() {
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;

		await runTransaction(eduDb, async (transaction) => {
			// Remove learning path from all associated workshops
			if (this.workshops && this.workshops.length > 0) {
				const batch = writeBatch(eduDb);
				for (const workshop of this.workshops) {
					const workshopRef = doc(eduDb, `tenants/${tenantId}/workshops`, `${workshop.id}`);
					const workshopDoc = await getDoc(workshopRef);
					if (workshopDoc.exists()) {
						const workshopData = workshopDoc.data();
						const learningPaths = workshopData.learningPaths || [];
						const updatedLearningPaths = learningPaths.filter(
							lp => lp.id !== this.id
						);
						batch.update(workshopRef, { learningPaths: updatedLearningPaths });
					}
				}
				await batch.commit();
			}

			const learningPathDocRef = doc(eduDb, `tenants/${tenantId}/learning-paths`, `${this.id}`);
			await transaction.delete(learningPathDocRef);
		});
	}
}
